import React, { useMemo, useState } from 'react';

/**
 * Reusable sortable table for KPI data grids.
 *
 * Props:
 *   columns   – [{ key, label, render?, align?, sortValue? }]
 *   data      – array of row objects
 *   maxHeight – optional max height for scroll container (default 500)
 *   numbered  – show a "#" column (default false)
 */
const SortableTable = ({ columns, data, maxHeight = 500, numbered = false }) => {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (key) => {
    if (sortCol === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(key);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    if (!sortCol) return data;
    const col = columns.find((c) => c.key === sortCol);
    return [...data].sort((a, b) => {
      const av = col?.sortValue ? col.sortValue(a) : a[sortCol];
      const bv = col?.sortValue ? col.sortValue(b) : b[sortCol];
      // numeric-aware compare
      const an = typeof av === 'number' ? av : parseFloat(String(av ?? '').replace(/[$,%]/g, ''));
      const bn = typeof bv === 'number' ? bv : parseFloat(String(bv ?? '').replace(/[$,%]/g, ''));
      if (!isNaN(an) && !isNaN(bn)) return sortDir === 'asc' ? an - bn : bn - an;
      const as = String(av ?? '').toLowerCase();
      const bs = String(bv ?? '').toLowerCase();
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
  }, [data, sortCol, sortDir, columns]);

  const thStyle = { position: 'sticky', top: 0, background: '#f8fafc', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

  return (
    <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight, border: '1px solid #e2e8f0', borderRadius: 6 }}>
      <table className="data-table">
        <thead>
          <tr>
            {numbered && <th style={{ ...thStyle, cursor: 'default' }}>#</th>}
            {columns.map((col) => (
              <th
                key={col.key}
                style={{ ...thStyle, textAlign: col.align || 'left' }}
                onClick={() => handleSort(col.key)}
                title={`Sort by ${col.label}`}
              >
                {col.label}
                {sortCol === col.key ? (
                  <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.7 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>
                ) : (
                  <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.25 }}>⇅</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr key={row._key ?? i}>
              {numbered && <td style={{ color: '#94a3b8', fontSize: 11 }}>{i + 1}</td>}
              {columns.map((col) => (
                <td key={col.key} style={{ textAlign: col.align || 'left', ...(col.tdStyle?.(row) || {}) }}>
                  {col.render ? col.render(row, i) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
          {sorted.length === 0 && (
            <tr><td colSpan={columns.length + (numbered ? 1 : 0)} style={{ textAlign: 'center', color: '#94a3b8', padding: 20 }}>No data</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default SortableTable;
